import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * 访问性增强组件
 * 自动添加ARIA标签，改进键盘导航，支持屏幕阅读器
 */
const AccessibilityEnhancements: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // 页面切换时更新标题和主要区域标签
    const mainContent = document.querySelector('main') || document.querySelector('[role="main"]');
    if (mainContent) {
      mainContent.setAttribute('role', 'main');
      mainContent.setAttribute('aria-label', `主要内容区域 - ${document.title}`);
    }

    // 为所有交互元素添加键盘导航支持
    const interactiveElements = document.querySelectorAll(
      'button, a, input, select, textarea, [role="button"], [tabindex]'
    );

    interactiveElements.forEach((element) => {
      // 确保可聚焦的元素有适当的tabindex
      if (!element.hasAttribute('tabindex') && element.tagName !== 'A' && element.tagName !== 'BUTTON') {
        if (element.getAttribute('role') === 'button') {
          element.setAttribute('tabindex', '0');
        }
      }

      // 添加键盘事件监听器
      const handleKeyDown = (e: KeyboardEvent) => {
        if ((e.key === 'Enter' || e.key === ' ') && element.getAttribute('role') === 'button') {
          e.preventDefault();
          (element as HTMLElement).click();
        }
      };

      element.addEventListener('keydown', handleKeyDown);
      return () => element.removeEventListener('keydown', handleKeyDown);
    });

    // 改进图片alt文本
    const images = document.querySelectorAll('img[alt=""]');
    images.forEach((img) => {
      const src = img.getAttribute('src') || '';
      const filename = src.split('/').pop() || '图片';
      img.setAttribute('alt', `装饰性图片: ${filename}`);
      img.setAttribute('aria-hidden', 'true');
    });

    // 改进表单标签
    const inputs = document.querySelectorAll('input, textarea, select');
    inputs.forEach((input) => {
      const id = input.getAttribute('id');
      const label = id ? document.querySelector(`label[for="${id}"]`) : null;
      if (!label && input.getAttribute('type') !== 'hidden') {
        const placeholder = input.getAttribute('placeholder');
        if (placeholder && !input.getAttribute('aria-label')) {
          input.setAttribute('aria-label', placeholder);
        }
      }
    });

    // 跳过链接（屏幕阅读器辅助）
    const skipLink = document.getElementById('skip-to-content');
    if (!skipLink) {
      const skip = document.createElement('a');
      skip.id = 'skip-to-content';
      skip.href = '#main-content';
      skip.className = 'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-teal-600 focus:text-white focus:rounded';
      skip.textContent = '跳过导航到主要内容';
      document.body.insertBefore(skip, document.body.firstChild);
    }

    // 改进颜色对比度提示（通过确保足够的对比度）
    const lowContrastElements = document.querySelectorAll('[style*="color"]');
    // 这里可以添加对比度检查逻辑

  }, [location]);

  return null;
};

export default AccessibilityEnhancements;


